import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { DNABox, util } from '@alucio/lux-ui';
import { useSelector } from 'react-redux';
import DNASlideRoll from 'src/components/DNA/SlideRoll/DNASlideRoll';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { SLIDE_ROLL_VARIANT } from 'src/components/DNA/SlideRoll/StyleSetting';
import { RootState } from 'src/state/redux';
import { useContent, LoadedPresentation } from 'src/state/context/ContentProvider/ContentProvider';
import { useMeetingPresentedMeta } from './State/MeetingPresentedMetaProvider';
import useCurrentPage from '../DNA/hooks/useCurrentPage';
import useHiddenSlideToggle from 'src/hooks/useHiddenSlideToggle';
import { isCustomDeckORM, isFolderItemORM } from 'src/types/typeguards';
import { PresentablePage } from 'src/types/types';

export const styles = StyleSheet.create({
  mainWrapper: {
    backgroundColor: colors['color-text-white'],
    paddingHorizontal: 16,
  },
  slideRoll: {
    display: 'none',
    minWidth: 332,
    maxWidth: 332,
    borderColor: colors['color-gray-100'],
    borderRightWidth: 1,
    backgroundColor: colors['color-text-white'],
  },
});

interface PreviewSlideRollConnectorProps {
  slideRollVisible: boolean
  closeButtonOnPress?: () => void,
}

interface Props {
  presentation: LoadedPresentation,
  onPageSelect: (presentationPageNumber: number) => void,
  onHiddenStateChange: (page: PresentablePage, isHidden: boolean) => void,
  closeButtonOnPress?: () => void,
}

const PreviewSlideRoll = (props: Props) => {
  const { presentation, onPageSelect, onHiddenStateChange, closeButtonOnPress } = props;
  const { presentedMetas, isPresentedSlideEmpty } = useMeetingPresentedMeta();
  const isMeetingHistoryMode = !!useSelector((state: RootState) => state.contentPreviewModal)?.meetingId;
  const route = useCurrentPage({ exact: true })
  const isPublisherRoute = !!route?.configOptions?.modules?.includes('publisher');
  const isCustomDeck = isFolderItemORM(presentation.presentable.orm) &&
    isCustomDeckORM(presentation.presentable.orm.relations.itemORM);
  const onToggleHidden = useHiddenSlideToggle({
    folderItemORM: isFolderItemORM(presentation.presentable.orm)
      ? presentation.presentable.orm
      : undefined,
  });

  const bookmarksOnly = presentation.pageNavigationView === 'bookmarks';
  const memoizedSlideRoll = useMemo(() => (
    <DNASlideRoll
      bookmarksOnly={bookmarksOnly}
      presentable={presentation.presentable}
      activeSlide={presentation.currentPresentablePage}
      presentedMeta={presentedMetas}
      variant={isPresentedSlideEmpty ? SLIDE_ROLL_VARIANT.PREVIEW_MODAL_EMPTY : SLIDE_ROLL_VARIANT.PREVIEW_MODAL}
      displayInsertTextBtn={!(isPublisherRoute || isMeetingHistoryMode)}
      displayGroupSelection
      onSelect={onPageSelect}
      onToggleHidden={isCustomDeck ? undefined : onToggleHidden}
      onHiddenStateChange={onHiddenStateChange}
    />
  ), [
    bookmarksOnly,
    isCustomDeck,
    isMeetingHistoryMode,
    isPresentedSlideEmpty,
    isPublisherRoute,
    onHiddenStateChange,
    onPageSelect,
    onToggleHidden,
    presentation,
    presentedMetas,
  ]);

  return (
    <DNABox fill appearance="col" testID="preview-slide-roll" style={styles.mainWrapper}>
      <DNASlideRoll.HeaderActionButtons
        isInContentPreviewModal
        title={bookmarksOnly ? 'Bookmarks' : undefined}
        onClose={closeButtonOnPress}
      />
      {memoizedSlideRoll}
    </DNABox>
  );
};

const PreviewSlideRollConnector: React.FC<PreviewSlideRollConnectorProps> = (props) => {
  const { closeButtonOnPress, slideRollVisible } = props;
  const {
    activePresentation,
    setActiveSlideByPresentationPageNumber,
    updateHiddenSlideState,
  } = useContent();
  const slideRollStyle = useMemo(() => util.mergeStyles(
    undefined,
    styles.slideRoll,
    [{ display: 'flex' }, slideRollVisible],
  ), [slideRollVisible]);

  if (!activePresentation) return null
  return (
    <DNABox fill style={slideRollStyle}>
      <PreviewSlideRoll
        presentation={activePresentation}
        onPageSelect={setActiveSlideByPresentationPageNumber}
        onHiddenStateChange={updateHiddenSlideState}
        closeButtonOnPress={closeButtonOnPress}
      />
    </DNABox>
  );
};

PreviewSlideRoll.title = 'PreviewSlideRoll';

export default PreviewSlideRollConnector;
